import { toast } from "react-toastify";
import {
  BridgeTimeoutError,
  getPrinterRoutes,
  getSavedPrinter,
  hasPrintBridge,
  sendToPrinter,
} from "./printBridge";
import { computeDelta, getRouteEntry, recordSent } from "./slipLedger";

const RECEIPT_STORAGE_KEY = "last_receipt";

// Default thermal roll width in mm (a template may override with 58).
const PAPER_MM = 80;

const esc = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// The cashier's user id is the `sub` claim of their login token (a JWT).
const getCashierId = () => {
  try {
    const token = sessionStorage.getItem("cashier_token");
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload)).sub || null;
  } catch {
    return null;
  }
};

// The last printed receipt is kept per cashier in localStorage, so it survives
// logout but another cashier on the same browser never sees it. If the cashier
// can't be identified it only lasts for the current login session.
const getReceiptStore = () => {
  const cashierId = getCashierId();
  return cashierId
    ? { storage: localStorage, key: `${RECEIPT_STORAGE_KEY}:${cashierId}` }
    : { storage: sessionStorage, key: RECEIPT_STORAGE_KEY };
};

export const saveLastReceipt = (receipt) => {
  try {
    const { storage, key } = getReceiptStore();
    storage.setItem(key, JSON.stringify(receipt));
    window.dispatchEvent(new Event("last-receipt-changed"));
  } catch {
    // Storage unavailable: reprint simply stays disabled.
  }
};

export const getLastReceipt = () => {
  try {
    const { storage, key } = getReceiptStore();
    return JSON.parse(storage.getItem(key));
  } catch {
    return null;
  }
};

export const formatMoney = (value, currency) =>
  `${currency ?? ""}${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const randomId = () => Math.random().toString(36).slice(2, 10);

// Dedupe key for the desktop print service (it drops a repeated key for 30s
// and while the first job is still queued). A sale keys on its order number so
// a double submit prints once. Drafts and reprints are intentional, so every
// click gets a fresh key. A sale with no order number must not share a key
// with other such sales, or real receipts would be dropped.
const makeJobKey = (kind, orderNo) => {
  if (kind === "sale" && orderNo) return `${orderNo}:sale`;
  return `${orderNo || "noorder"}:${kind}:${Date.now()}-${randomId()}`;
};

const headerLinesOf = (template) => [
  template?.header_line_one || "01POS",
  template?.header_line_two || "",
  template?.header_line_three || "",
];

// Builds the receipt v1 payload agreed with the desktop app. Money goes as
// display strings: the desktop app prints them as is and does no maths.
export const buildReceiptPayload = ({
  receipt,
  template,
  currency,
  kind,
  printerName,
}) => {
  const resolvedKind = kind || (receipt?.isDraft ? "draft" : "sale");
  const orderNo = receipt?.orderNo ? String(receipt.orderNo) : "";

  return {
    version: 1,
    jobKey: makeJobKey(resolvedKind, orderNo),
    printerName,
    kind: resolvedKind,
    copies: Math.max(1, Number(template?.number_of_pages) || 1),
    receipt: {
      headerLines: headerLinesOf(template),
      orderNo: orderNo || "-",
      date: String(receipt?.date ?? ""),
      time: String(receipt?.time ?? ""),
      items: (receipt?.items || []).map((item) => {
        const line = {
          name: String(item?.name ?? ""),
          quantity: Number(item?.quantity) || 0,
          total: formatMoney(item?.total, currency),
        };
        // Only used for per-category routing later; omitted when unknown.
        if (item?.categoryId != null && item.categoryId !== "") {
          line.categoryId = Number(item.categoryId);
        }
        return line;
      }),
      autoBill: (receipt?.autoBill || []).map((bill) => ({
        name: String(bill?.name ?? ""),
        amount: formatMoney(bill?.amount, currency),
      })),
      total: formatMoney(receipt?.total, currency),
      paymentMethod:
        resolvedKind === "draft" ? "" : String(receipt?.paymentType ?? ""),
      footerLines: [
        template?.footer_line_one || "",
        template?.footer_line_two || "",
        template?.footer_line_three || "",
      ],
    },
  };
};

// Sends the receipt to the desktop app's printer when running inside it with a
// printer chosen, otherwise opens the browser print dialog.
// kind: "sale" | "draft" | "reprint" (defaults from receipt.isDraft).
// Resolves to "bridge", "browser" or "unconfirmed" (bridge timed out).
const printCustomerReceipt = async ({ receipt, template, currency, kind }) => {
  const printerName = getSavedPrinter();

  if (!hasPrintBridge()) {
    printReceiptInBrowser({ receipt, template, currency });
    return "browser";
  }

  if (!printerName) {
    toast.info(
      "No receipt printer chosen. Pick one under Printer settings (printer icon)."
    );
    printReceiptInBrowser({ receipt, template, currency });
    return "browser";
  }

  try {
    const payload = buildReceiptPayload({
      receipt,
      template,
      currency,
      kind,
      printerName,
    });
    const result = await sendToPrinter(payload);
    if (result?.status && result.status !== "Queued") {
      toast.info(`Printer: ${result.message || result.status}`);
    }
    return "bridge";
  } catch (error) {
    // A timeout may still have queued the job, so falling back here could
    // print twice. Let the cashier decide.
    if (error instanceof BridgeTimeoutError) {
      toast.warn(
        "The printer service didn't respond. If nothing prints, use Reprint."
      );
      return "unconfirmed";
    }
    toast.error(
      `Couldn't send to ${printerName}: ${error?.message || "unknown error"}. Opening the print dialog instead.`
    );
    printReceiptInBrowser({ receipt, template, currency });
    return "browser";
  }
};

// ---- Extra printers (Pro) -------------------------------------------------

// A kitchen/bar slip: receipt v1, kind "ticket", no prices or payment.
// `items` are what to print now (already reduced by the ledger); `n` is the
// ordinal of this slip for the route and order; `addOn` marks a later slip.
export const buildSlipPayload = ({ receipt, template, route, items, n, addOn }) => {
  const orderNo = receipt?.orderNo ? String(receipt.orderNo) : "";
  return {
    version: 1,
    jobKey: orderNo
      ? `${orderNo}:ticket:${route.printerName}:${n}`
      : `noorder:ticket:${route.printerName}:${Date.now()}-${randomId()}`,
    printerName: route.printerName,
    kind: "ticket",
    copies: 1,
    receipt: {
      headerLines: headerLinesOf(template),
      title: addOn ? "ADD-ON" : "",
      orderNo: orderNo || "-",
      date: String(receipt?.date ?? ""),
      time: String(receipt?.time ?? ""),
      items: items.map((item) => {
        const line = {
          name: String(item?.name ?? ""),
          quantity: Number(item?.quantity) || 0,
        };
        if (item?.categoryId != null && item.categoryId !== "") {
          line.categoryId = Number(item.categoryId);
        }
        return line;
      }),
      autoBill: [],
      total: "",
      paymentMethod: "",
      footerLines: ["", "", ""],
    },
  };
};

// The items a route is responsible for. Only "assigned" slip routes filter by
// category (cat_id); everything else takes all items.
export const itemsForRoute = (route, receipt) => {
  const items = receipt?.items || [];
  if (route.type !== "slip" || route.items !== "assigned") return items;
  const categoryIds = new Set(route.categoryIds);
  return items.filter(
    (item) =>
      item?.categoryId != null &&
      item.categoryId !== "" &&
      categoryIds.has(Number(item.categoryId))
  );
};

const routeFailed = (route, error) =>
  toast.error(
    `Couldn't print to ${route.printerName}: ${error?.message || "unknown error"}`
  );

// Orders+routes with a job on the way. A second click while the first is still
// with the bridge would otherwise print the same items twice.
const inFlight = new Set();

const sendReceiptToRoute = async ({ route, receipt, template, currency, kind }) => {
  const payload = buildReceiptPayload({
    receipt,
    template,
    currency,
    kind,
    printerName: route.printerName,
  });
  // A paid sale is one job per order. A draft print is intentional every click
  // (like drafts on the main printer), so it gets a fresh key each time.
  if (kind === "draft") {
    payload.jobKey = `${receipt?.orderNo || "noorder"}:draft:${route.printerName}:${Date.now()}-${randomId()}`;
  } else if (receipt?.orderNo) {
    payload.jobKey = `${receipt.orderNo}:sale:${route.printerName}`;
  }
  await sendToPrinter(payload);
  return payload;
};

const sendSlipToRoute = async ({ route, receipt, template }) => {
  const orderNo = receipt?.orderNo ? String(receipt.orderNo) : "";
  const entry = orderNo ? getRouteEntry(orderNo, route.id) : { n: 0, items: {} };
  const items = computeDelta(entry, itemsForRoute(route, receipt));
  if (items.length === 0) return null; // nothing new for this route

  const payload = buildSlipPayload({
    receipt,
    template,
    route,
    items,
    n: entry.n + 1,
    addOn: entry.n > 0,
  });
  await sendToPrinter(payload);
  if (orderNo) recordSent(orderNo, route.id, items); // only once the bridge accepted it
  return payload;
};

// Prints on the Pro extra printers. event: "draft" (Print on a draft) or
// "paid" (after a successful sale). Only inside the desktop app, only when
// routes exist, and only for Pro (checked now via checkPro; see fetchIsPro for
// how an unknown plan is handled). Failures toast
// the printer's name; the browser print dialog is never used.
export const printExtraPrinters = async ({
  receipt,
  template,
  currency,
  event,
  checkPro,
}) => {
  if (!hasPrintBridge()) return [];
  const routes = getPrinterRoutes().filter((route) => route.printOn[event]);
  if (routes.length === 0) return [];

  // checkPro resolves true (Pro), false (a profile says not Pro) or null / throws
  // (plan can't be confirmed). Only a confirmed non-Pro plan is silent.
  let isPro = null;
  try {
    isPro = await checkPro?.();
  } catch {
    isPro = null;
  }
  if (isPro === false || (isPro == null && !checkPro)) return [];
  if (isPro == null) {
    toast.error("Couldn't confirm your plan, kitchen/bar slips not sent.");
    return [];
  }

  const sent = [];
  for (const route of routes) {
    const lockKey = `${receipt?.orderNo || "noorder"}:${route.id}`;
    if (inFlight.has(lockKey)) continue;
    inFlight.add(lockKey);
    try {
      const payload =
        route.type === "receipt"
          ? await sendReceiptToRoute({
              route,
              receipt,
              template,
              currency,
              kind: event === "paid" ? "sale" : "draft",
            })
          : await sendSlipToRoute({ route, receipt, template });
      if (payload) sent.push(payload);
    } catch (error) {
      routeFailed(route, error);
    } finally {
      inFlight.delete(lockKey);
    }
  }
  return sent;
};

// kind: "sale" | "draft" | "reprint" (defaults from receipt.isDraft).
// The main printer always gets the customer receipt. Extra printers run after
// it for a paid sale or a draft print, never for a reprint, and only when the
// caller passes checkPro (an async () => boolean).
export const printReceipt = async ({
  receipt,
  template,
  currency,
  kind,
  checkPro,
}) => {
  const result = await printCustomerReceipt({ receipt, template, currency, kind });
  const resolvedKind = kind || (receipt?.isDraft ? "draft" : "sale");
  const event = resolvedKind === "sale" ? "paid" : resolvedKind === "draft" ? "draft" : null;
  if (event && checkPro) {
    try {
      await printExtraPrinters({ receipt, template, currency, event, checkPro });
    } catch (error) {
      console.error("Extra printers failed:", error);
    }
  }
  return result;
};

// receipt: { orderNo, date, time, items: [{ name, quantity, total }],
//   autoBill: [{ name, amount }], total, paymentType, isDraft }
const printReceiptInBrowser = ({ receipt, template, currency }) => {
  // Roll width in mm. A template may carry a paper width (80 or 58); anything
  // else falls back to PAPER_MM. Printable area is the roll minus ~4mm per side.
  const templateMm = parseInt(
    template?.paper_width ?? template?.paper_size ?? template?.paper_mm,
    10
  );
  const paperMm = templateMm === 58 || templateMm === 80 ? templateMm : PAPER_MM;
  const printableMm = paperMm === 58 ? 48 : 72;

  const money = (value) => formatMoney(value, currency);

  const generateReceiptContent = () => `
    <section class="receipt">
      <div class="center title">${esc(template?.header_line_one || "01POS")}</div>
      ${receipt.isDraft ? `<div class="center bold">DRAFT ORDER - NOT PAID</div>` : ""}
      ${template?.header_line_two ? `<div class="center">${esc(template.header_line_two)}</div>` : ""}
      ${template?.header_line_three ? `<div class="center">${esc(template.header_line_three)}</div>` : ""}
      <div class="rule"></div>
      <div class="row"><span>Order No:</span><span class="amt bold">${esc(receipt.orderNo || "-")}</span></div>
      <div class="row"><span>Date:</span><span class="amt">${esc(receipt.date)}</span></div>
      <div class="row"><span>Time:</span><span class="amt">${esc(receipt.time)}</span></div>
      <div class="rule"></div>
      <div class="row head"><span class="name">Item</span><span class="qty">Qty</span><span class="amt">Total</span></div>
      ${receipt.items
        .map(
          (item) => `
        <div class="row item"><span class="name">${esc(item.name)}</span><span class="qty">${esc(item.quantity)}</span><span class="amt">${money(item.total)}</span></div>`
        )
        .join("")}
      ${
        receipt.autoBill?.length > 0
          ? `<div class="rule"></div>${receipt.autoBill
              .map(
                (item) => `
        <div class="row"><span class="name">${esc(item.name)}</span><span class="amt">${money(item.amount)}</span></div>`
              )
              .join("")}`
          : ""
      }
      <div class="rule"></div>
      <div class="row total"><span>TOTAL</span><span class="amt">${money(receipt.total)}</span></div>
      ${receipt.isDraft ? "" : `<div class="row"><span>Payment method:</span><span class="amt bold">${esc(receipt.paymentType)}</span></div>`}
      <div class="rule"></div>
      <div class="center footer">
        ${template?.footer_line_one ? `<div class="bold">${esc(template.footer_line_one)}</div>` : ""}
        ${template?.footer_line_two ? `<div>${esc(template.footer_line_two)}</div>` : ""}
        ${template?.footer_line_three ? `<div>${esc(template.footer_line_three)}</div>` : ""}
        <div class="powered">Powered by 01POS</div>
      </div>
    </section>
  `;

  const numCopies = Number(template?.number_of_pages) || 1;
  let allReceipts = "";

  for (let i = 0; i < numCopies; i++) {
    allReceipts += generateReceiptContent();
  }

  // Open new print window
  const printWindow = window.open("", "_blank", "width=600,height=800");
  if (printWindow) {
    printWindow.document.write(`<!doctype html>
      <html lang="en">
      <head>
          <meta charset="utf-8">
          <title>Receipt</title>
          <style>
            @page { size: ${paperMm}mm auto; margin: 0; }
            * { box-sizing: border-box; }
            html, body { margin: 0; padding: 0; background: #fff; }
            body {
              width: ${paperMm}mm;
              color: #000;
              font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, 'Liberation Mono', monospace;
              font-size: 12px;
              line-height: 1.3;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .receipt {
              width: ${paperMm}mm;
              padding: 2mm ${(paperMm - printableMm) / 2}mm 3mm;
            }
            .receipt + .receipt { break-before: page; page-break-before: always; }
            .center { text-align: center; overflow-wrap: anywhere; }
            .title { font-size: 16px; font-weight: 700; }
            .bold { font-weight: 700; }
            .rule { border-top: 1px dashed #000; margin: 5px 0; }
            .row { display: flex; align-items: flex-start; gap: 8px; }
            .row > span:first-child { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
            .amt { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
            .qty { flex: 0 0 auto; min-width: 3ch; text-align: right; font-variant-numeric: tabular-nums; }
            .head { font-weight: 700; text-transform: uppercase; font-size: 11px; }
            .item { padding: 1px 0; }
            .total { font-size: 15px; font-weight: 700; }
            .footer { margin-top: 2px; }
            .powered { margin-top: 4px; font-size: 10px; }
          </style>
      </head>
      <body>
          ${allReceipts}
      </body>
      </html>
    `);
    printWindow.document.close();
    // Closing straight after print() can cancel the job in some browsers.
    printWindow.onafterprint = () => printWindow.close();
    printWindow.print();
  } else {
    console.error("Pop-up blocked! Allow pop-ups to print receipts.");
  }
};

export const receiptFromDraft = (draft) => {
  const items = (draft?.orderdetails || []).map((detail) => ({
    name: detail?.product_name || detail?.product?.product_name,
    quantity: detail?.quantity_ordered,
    total: Number(detail?.unit_price) * Number(detail?.quantity_ordered),
    categoryId: detail?.product?.cat_id ?? detail?.cat_id ?? null,
    productId: detail?.product_id ?? null,
  }));

  return {
    orderNo: draft?.orderNO,
    date: new Date(draft?.updated_at).toLocaleDateString(),
    time: new Date(draft?.updated_at).toLocaleTimeString(),
    items,
    autoBill: [],
    total: draft?.total_price,
    paymentType: "",
    isDraft: true,
  };
};
